import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Search, AlertTriangle, CheckCircle2, ExternalLink, Copy, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://vlmagnetwork.com";
const TITLE_MAX = 60;
const DESC_MAX = 155;

const Field = ({ label, value, max, onChange, testid, textarea }) => {
  const over = value.length > max;
  const cls = "w-full bg-black border border-white/15 text-white text-sm px-3 py-2 outline-none focus:border-[#E30613]";
  return (
    <label className="block">
      <span className="flex justify-between text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}<span data-testid={`${testid}-count`} className={over ? "text-amber-400" : "text-emerald-400"}>{value.length}/{max}</span></span>
      {textarea ? <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={2} className={cls} /> : <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />}
    </label>
  );
};

const mediaUrl = (src) => (src && src.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src || "");
const clip = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s || "");

function issues(story) {
  const out = [];
  const title = `${story.title} — ${story.network}`;
  if (title.length > TITLE_MAX) out.push(`Title is ${title.length} chars — Google cuts around ${TITLE_MAX}`);
  if (!story.excerpt) out.push("No excerpt — Google & social will show nothing");
  else if (story.excerpt.length > DESC_MAX) out.push(`Excerpt is ${story.excerpt.length} chars — trimmed after ${DESC_MAX}`);
  if (!story.og_image && !story.image) out.push("No cover image — social cards will be blank");
  if (!story.og_image) out.push("No branded share card yet — raw cover will be used");
  if (story.published === false || (story.status && story.status !== "published")) out.push("Not published — hidden from Google");
  return out;
}

const GooglePreview = ({ story }) => (
  <div data-testid="seo-google-preview" className="bg-white text-black p-5 rounded-sm">
    <div className="flex items-center gap-2 mb-1.5">
      <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center text-[10px] font-black"><span className="text-[#E30613]">VL</span><span className="text-white">M</span></div>
      <div className="leading-tight">
        <div className="text-[14px] text-[#202124]">VLMAG</div>
        <div className="text-[12px] text-[#4d5156]">{SITE} › story › {story.id}</div>
      </div>
    </div>
    <div className="text-[20px] text-[#1a0dab] leading-snug hover:underline cursor-pointer">{clip(`${story.title} — ${story.network}`, TITLE_MAX + 5)}</div>
    <div className="text-[14px] text-[#4d5156] leading-[1.58] mt-1">{clip(story.excerpt, DESC_MAX + 5) || <span className="italic text-neutral-400">No description</span>}</div>
  </div>
);

const SocialCard = ({ story, label, testid, rounded }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">{label}</p>
    <div className={`overflow-hidden border border-white/15 bg-[#0f0f0f] max-w-md ${rounded ? "rounded-2xl" : ""}`}>
      <div className="aspect-[1.91/1] bg-neutral-900">
        {(story.og_image || story.image) && <img src={mediaUrl(story.og_image || story.image)} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="p-3 border-t border-white/10">
        <div className="text-[10px] uppercase tracking-wider text-neutral-500">vlmagnetwork.com</div>
        <div className="text-sm font-bold text-white leading-snug line-clamp-1">{story.title}</div>
        <div className="text-xs text-neutral-400 line-clamp-2 mt-0.5">{story.excerpt}</div>
      </div>
    </div>
  </div>
);

function needsTrim(story) {
  return `${story.title} — ${story.network}`.length > TITLE_MAX || (story.excerpt || "").length > DESC_MAX;
}

function toPayload(story, patch) {
  const { id, share_clicks, created_at, ...rest } = story;
  return { ...rest, ...patch };
}

export const SeoPreview = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [copied, setCopied] = useState(false);
  const [suggest, setSuggest] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [bulk, setBulk] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/stories`).then((r) => { setStories(r.data); setSelectedId(r.data[0]?.id || ""); }).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setSuggest(null); setError(""); }, [selectedId]);

  const filtered = useMemo(() => stories.filter((s) => s.title.toLowerCase().includes(q.toLowerCase())), [stories, q]);
  const story = stories.find((s) => s.id === selectedId);
  const shareUrl = story ? `${process.env.REACT_APP_BACKEND_URL}/api/share/${story.id}` : "";

  const copy = () => { navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const runSuggest = async () => {
    setBusy("suggest"); setError("");
    try {
      const r = await axios.post(`${API}/admin/stories/${story.id}/seo-suggest`);
      setSuggest(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not generate a suggestion.");
    } finally { setBusy(""); }
  };

  const applySuggest = async () => {
    setBusy("apply"); setError("");
    try {
      const r = await axios.put(`${API}/stories/${story.id}`, toPayload(story, { title: suggest.title, excerpt: suggest.excerpt }));
      setStories((prev) => prev.map((s) => (s.id === story.id ? r.data : s)));
      setSuggest(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not save changes.");
    } finally { setBusy(""); }
  };

  const flagged = stories.filter(needsTrim);

  const runBulk = async () => {
    const items = flagged.map((s) => ({ id: s.id, network: s.network, from: s.title, title: "", excerpt: "", state: "pending" }));
    setBulk({ items, phase: "suggesting" });
    for (const it of items) {
      try {
        const r = await axios.post(`${API}/admin/stories/${it.id}/seo-suggest`);
        Object.assign(it, { title: r.data.title, excerpt: r.data.excerpt, title_budget: r.data.title_budget, excerpt_budget: r.data.excerpt_budget, state: "ready" });
      } catch { it.state = "failed"; }
      setBulk({ items: [...items], phase: "suggesting" });
    }
    setBulk({ items: [...items], phase: "review" });
  };

  const applyBulk = async () => {
    const items = bulk.items;
    setBulk({ items, phase: "applying" });
    let applied = 0;
    for (const it of items.filter((i) => i.state === "ready")) {
      const s = stories.find((x) => x.id === it.id);
      try {
        const r = await axios.put(`${API}/stories/${it.id}`, toPayload(s, { title: it.title, excerpt: it.excerpt }));
        setStories((prev) => prev.map((x) => (x.id === it.id ? r.data : x)));
        it.state = "applied"; applied += 1;
      } catch { it.state = "failed"; }
      setBulk({ items: [...items], phase: "applying" });
    }
    setBulk({ items: [...items], phase: "done", applied });
  };

  const editBulk = (id, patch) => setBulk((b) => ({ ...b, items: b.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));

  if (loading) return <div className="flex items-center gap-2 text-neutral-400 text-sm"><Loader2 className="animate-spin" size={16} /> Loading stories…</div>;

  return (
    <div data-testid="seo-preview" className="grid lg:grid-cols-[320px_1fr] gap-8">
      <aside>
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <Input data-testid="seo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search stories" className="pl-9 bg-black border-white/15 text-white" />
        </div>
        <button data-testid="seo-bulk-fix" onClick={runBulk} disabled={!flagged.length || (bulk && bulk.phase !== "done" && bulk.phase !== "review")}
          className="w-full mb-3 inline-flex items-center justify-center gap-2 border border-[#E30613] text-white text-[10px] uppercase tracking-wider font-bold px-4 py-2.5 hover:bg-[#E30613] transition-colors disabled:opacity-40 disabled:hover:bg-transparent">
          <Sparkles size={12} /> {flagged.length ? `Fix all flagged (${flagged.length})` : "Nothing to fix"}
        </button>
        <ul className="border border-white/10 divide-y divide-white/10 max-h-[70vh] overflow-y-auto">
          {filtered.map((s) => {
            const n = issues(s).length;
            return (
              <li key={s.id}>
                <button data-testid={`seo-story-${s.id}`} onClick={() => setSelectedId(s.id)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${s.id === selectedId ? "bg-white/10" : "hover:bg-white/5"}`}>
                  {n ? <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" /> : <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 shrink-0" />}
                  <span>
                    <span className="block text-sm text-white leading-snug line-clamp-2">{s.title}</span>
                    <span className="block text-[10px] uppercase tracking-wider text-neutral-500 mt-0.5">{s.network}{n ? ` · ${n} issue${n > 1 ? "s" : ""}` : " · Looks good"}</span>
                  </span>
                </button>
              </li>
            );
          })}
          {!filtered.length && <li className="px-4 py-6 text-sm text-neutral-500">No stories match.</li>}
        </ul>
      </aside>

      {bulk ? (
        <section data-testid="seo-bulk-panel" className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613]">Bulk snippet fix</p>
              <p data-testid="seo-bulk-status" className="text-sm text-neutral-300 mt-1">
                {bulk.phase === "suggesting" && <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Shortening {bulk.items.filter((i) => i.state !== "pending").length}/{bulk.items.length}…</span>}
                {bulk.phase === "review" && `${bulk.items.filter((i) => i.state === "ready").length} suggestions ready — review, edit, then apply.`}
                {bulk.phase === "applying" && <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Saving…</span>}
                {bulk.phase === "done" && `Done — ${bulk.applied} ${bulk.applied === 1 ? "story" : "stories"} updated.`}
              </p>
            </div>
            <div className="flex gap-3">
              {bulk.phase === "review" && (
                <button data-testid="seo-bulk-apply" onClick={applyBulk} className="inline-flex items-center gap-2 bg-white text-black text-[10px] uppercase tracking-wider font-bold px-4 py-2 hover:bg-[#E30613] hover:text-white transition-colors"><CheckCircle2 size={12} /> Apply all</button>
              )}
              {(bulk.phase === "review" || bulk.phase === "done") && (
                <button data-testid="seo-bulk-close" onClick={() => setBulk(null)} className="text-[10px] uppercase tracking-wider font-bold text-neutral-400 hover:text-white px-2">{bulk.phase === "done" ? "Close" : "Cancel"}</button>
              )}
            </div>
          </div>
          <ul className="space-y-3">
            {bulk.items.map((it) => (
              <li key={it.id} data-testid={`seo-bulk-item-${it.id}`} className={`border p-4 space-y-3 ${it.state === "applied" ? "border-emerald-500/40" : it.state === "failed" ? "border-red-500/40" : "border-white/10"}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs text-neutral-500 line-clamp-1"><span className="text-neutral-300">{it.from}</span> · {it.network}</p>
                  <span className={`text-[10px] uppercase tracking-wider font-bold shrink-0 ${it.state === "applied" ? "text-emerald-400" : it.state === "failed" ? "text-red-400" : it.state === "ready" ? "text-white" : "text-neutral-500"}`}>
                    {it.state === "pending" ? "Waiting" : it.state === "ready" ? "Ready" : it.state === "applied" ? "Applied" : "Failed"}
                  </span>
                </div>
                {it.state === "ready" && bulk.phase === "review" && (
                  <>
                    <Field label="Title" value={it.title} max={it.title_budget} onChange={(v) => editBulk(it.id, { title: v })} testid={`seo-bulk-title-${it.id}`} />
                    <Field label="Excerpt" value={it.excerpt} max={it.excerpt_budget} onChange={(v) => editBulk(it.id, { excerpt: v })} testid={`seo-bulk-excerpt-${it.id}`} textarea />
                  </>
                )}
                {(it.state === "applied" || (it.state === "ready" && bulk.phase !== "review")) && <p className="text-sm text-white">{it.title}</p>}
              </li>
            ))}
          </ul>
        </section>
      ) : story ? (
        <section className="space-y-8">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Google search result</p>
            <GooglePreview story={story} />
            <div className="flex flex-wrap gap-4 mt-2 text-[11px] text-neutral-500">
              <span data-testid="seo-title-count" className={`${story.title.length + story.network.length + 3 > TITLE_MAX ? "text-amber-400" : ""}`}>Title {story.title.length + story.network.length + 3}/{TITLE_MAX}</span>
              <span data-testid="seo-desc-count" className={`${(story.excerpt || "").length > DESC_MAX ? "text-amber-400" : ""}`}>Description {(story.excerpt || "").length}/{DESC_MAX}</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <SocialCard story={story} label="X / Facebook / LinkedIn" testid="seo-social-preview" />
            <SocialCard story={story} label="iMessage / WhatsApp / Instagram DM" testid="seo-message-preview" rounded />
          </div>

          <div className="border border-white/10 p-5 space-y-3">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500">Checks</p>
            {issues(story).length ? (
              <ul data-testid="seo-issues" className="space-y-1.5">
                {issues(story).map((i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-300"><AlertTriangle size={14} className="mt-0.5 shrink-0" /> {i}</li>)}
              </ul>
            ) : (
              <p data-testid="seo-issues" className="flex items-center gap-2 text-sm text-emerald-400"><CheckCircle2 size={14} /> Title, description and cover all within limits.</p>
            )}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button data-testid="seo-auto-fix" onClick={runSuggest} disabled={!!busy}
                className="inline-flex items-center gap-2 bg-[#E30613] text-white text-[10px] uppercase tracking-wider font-bold px-4 py-2 hover:bg-white hover:text-black transition-colors disabled:opacity-50">
                {busy === "suggest" ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Shorten for Google
              </button>
              {error && <span data-testid="seo-error" className="text-xs text-red-400">{error}</span>}
            </div>

            {suggest && (
              <div data-testid="seo-suggestion" className="border border-[#E30613]/50 bg-[#E30613]/5 p-4 space-y-3">
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#E30613]">Suggested — review before applying</p>
                <Field label="Title" value={suggest.title} max={suggest.title_budget} onChange={(v) => setSuggest({ ...suggest, title: v })} testid="seo-suggest-title" />
                <Field label="Excerpt" value={suggest.excerpt} max={suggest.excerpt_budget} onChange={(v) => setSuggest({ ...suggest, excerpt: v })} testid="seo-suggest-excerpt" textarea />
                <div className="flex gap-3">
                  <button data-testid="seo-apply" onClick={applySuggest} disabled={!!busy}
                    className="inline-flex items-center gap-2 bg-white text-black text-[10px] uppercase tracking-wider font-bold px-4 py-2 hover:bg-[#E30613] hover:text-white transition-colors disabled:opacity-50">
                    {busy === "apply" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Apply to story
                  </button>
                  <button data-testid="seo-discard" onClick={() => setSuggest(null)} className="text-[10px] uppercase tracking-wider font-bold text-neutral-400 hover:text-white px-2">Discard</button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/10">
              <code className="text-xs text-neutral-400 break-all">{shareUrl}</code>
              <button data-testid="seo-copy-share" onClick={copy} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-white border border-white/20 px-3 py-1.5 hover:bg-white/10"><Copy size={12} /> {copied ? "Copied" : "Copy share link"}</button>
              <a data-testid="seo-open-share" href={shareUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[#E30613] hover:underline"><ExternalLink size={12} /> Open raw card</a>
              <a data-testid="seo-rich-results" href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(`${SITE}/api/share/${story.id}`)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-neutral-300 hover:underline"><ExternalLink size={12} /> Test in Google</a>
            </div>
          </div>
        </section>
      ) : <p className="text-neutral-500 text-sm">Select a story to preview.</p>}
    </div>
  );
};
